// A video's address by its speakers' names (Seán, 2026-11-05), the canonical one; its hash
// address forwards to it (views/Videos/index.vue):
//   /Videos/Brady_Johnston                     the speaker's one talk
//   /Videos/Seán_O'Donoghue                    a speaker with several talks: the gallery of them
//   /Videos/Seán_O'Donoghue/2024               one of them, by year
//   /Videos/Christian_Stolte/2025/Masterclass  two in one year: the event (Short, Masterclass…)
//   /Videos/Monica_Zoppè/2011/Part-1           or the part
//   /Videos/Fernanda_Viégas_&_Martin_Wattenberg/2011  a shared talk: both names, "_&_"
//   /Videos/Showreel/2024, Hero_Animation/2025, Closing_Remarks/2025  no speaker: what it is
// Spelling: the names' accents kept (the spelling with most of them, where titles differ); an
// address without them, or in other capitals, finds the same video. A video whose title names
// no speaker keeps its hash address.
import {getSpeakerKey, getVideoSpeakerKeys} from "@/composables/useSpeakerIndex";

const EVENTS = {Shorts: "Short", "VIZBI Showcase": "Showcase"};
const PARTS = {one: 1, two: 2, three: 3, 1: 1, 2: 2, 3: 3};
const cache = new WeakMap(); // showcases → their paths, built once

const getSlug = (text) => text.trim().replace(/\s+/g, "_");
const getAccents = (text) => (text.normalize("NFD").match(/[̀-ͯ]/g) || []).length;
const getTitleCase = (text) => text.replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
// a path after /Videos/, whatever its spelling: "Sean_O'Donoghue/2024" → "sean o'donoghue/2024"
export const getPathKey = (path) => getSpeakerKey(String(path).replaceAll("_", " "));

// "VIZBI 2024 showreel" → "Showreel/2024",
// "VIZBI 2025: Closing remarks" → "Closing_Remarks/2025"
function getUnnamedPath(name) {
  const match = name.match(/^VIZBI (\d{4}):? (.+)$/);
  return match ? `${getSlug(getTitleCase(match[2]))}/${match[1]}` : "";
}

// [{hash, year, event, title, names}] for the library's videos (not the films: Video/films.js)
function getVideos(showcases) {
  const videos = [];
  for (const [showcaseName, showcase] of Object.entries(showcases)) {
    const year = showcaseName.match(/\d{4}$/)?.[0] || "";
    const event = showcaseName.replace(/ ?\d{4}$/, "");
    for (const [hash, video] of Object.entries(showcase.videos || {})) {
      if (hash.includes("_")) continue;
      const [speakerPart, ...titleParts] = String(video.name || "").split(": ");
      const isNamed = titleParts.length && !/^VIZBI\b/.test(speakerPart);
      const names = isNamed ? speakerPart.split(" & ") : [];
      videos.push({hash, year, event, title: titleParts.join(": "), names, name: video.name});
    }
  }
  return videos;
}

// each speaker's spelling with the most accents ("Seán" over "Sean")
function getSpellings(videos) {
  const spellings = {};
  for (const name of videos.flatMap((video) => video.names.map((each) => each.trim()))) {
    const key = getSpeakerKey(name);
    if (!spellings[key] || getAccents(name) > getAccents(spellings[key])) spellings[key] = name;
  }
  return spellings;
}

// "Part One", "Part 2" → "Part-1"; else the event, as its collection is named ("Short")
function getQualifier(video) {
  const part = video.title.match(/\bPart (\w+)/i)?.[1]?.toLowerCase();
  if (PARTS[part]) return `Part-${PARTS[part]}`;
  return getSlug(EVENTS[video.event] || video.event);
}

// a base (the names) holding one video is that video; more: by year, then by qualifier
// the videos a gallery of these speakers lists: every one they're all named in
function getListed(videos, keys) {
  return videos.filter((video) => {
    return keys.every((key) => getVideoSpeakerKeys(video.name).includes(key));
  });
}

function addSpeakerPaths(paths, videos, bases) {
  const byBase = {};
  for (const [video, base] of bases) (byBase[base] ||= []).push(video);
  for (const [base, group] of Object.entries(byBase)) {
    const listed = getListed(videos, getPathKey(base).split(" & "));
    for (const video of group) {
      if (listed.length === 1) paths.set(video.hash, base);
      else if (group.filter((other) => other.year === video.year).length === 1) {
        paths.set(video.hash, `${base}/${video.year}`);
      } else paths.set(video.hash, `${base}/${video.year}/${getQualifier(video)}`);
    }
  }
}

// {byHash: Map(hash → path), byKey: Map(path key → hash), spellings: {speaker key → name}}
function buildPaths(showcases) {
  const videos = getVideos(showcases);
  const spellings = getSpellings(videos);
  const paths = new Map();
  const bases = new Map();
  for (const video of videos) {
    if (video.names.length) {
      const names = video.names.map((name) => spellings[getSpeakerKey(name)]);
      bases.set(video, names.map(getSlug).join("_&_"));
    } else if (getUnnamedPath(video.name)) paths.set(video.hash, getUnnamedPath(video.name));
  }
  addSpeakerPaths(paths, videos, bases);
  const byKey = new Map([...paths].map(([hash, path]) => [getPathKey(path), hash]));
  return {byHash: paths, byKey, spellings, videos};
}

function getPaths(showcases) {
  if (!cache.has(showcases)) cache.set(showcases, buildPaths(showcases));
  return cache.get(showcases);
}

// the video's canonical path after /Videos/, or "" (none: its hash address stays)
export function getVideoPath(showcases, hash) {
  if (!showcases || !Object.keys(showcases).length) return "";
  return getPaths(showcases).byHash.get(hash) || "";
}

// the video at a path after /Videos/, or ""; a speaker's year also finds a talk shared with
// another speaker (/Videos/Seán_O'Donoghue/2026 → his talk with Christian Stolte)
export function getPathHash(showcases, path) {
  if (!showcases || !Object.keys(showcases).length || !path) return "";
  const {byKey, videos} = getPaths(showcases);
  const key = getPathKey(path);
  if (byKey.has(key)) return byKey.get(key);
  const [base, year, ...rest] = key.split("/");
  if (!year || rest.length) return "";
  const found = getListed(videos, base.split(" & ")).filter((video) => video.year === year);
  return found.length === 1 ? found[0].hash : "";
}

// a speaker gallery's path in its canonical spelling ("sean_o'donoghue" → "Seán_O'Donoghue")
export function getSpeakerPath(showcases, speaker) {
  if (!showcases || !Object.keys(showcases).length) return speaker;
  const {spellings} = getPaths(showcases);
  const names = getPathKey(speaker).split(" & ").map((key) => spellings[key]);
  return names.every(Boolean) ? names.map(getSlug).join("_&_") : speaker;
}
